import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import CacheBuster from 'react-cache-buster';
import { createFidesTheme, FidesTheme, ThemeProvider, FidesThemeOptions } from '@fides/fides-react-components';
import fidesThemeOptionsJson from 'core/assets/jsons/fidesThemeOptions.json';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { I18nextProvider } from 'react-i18next';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { GlobalConfigProvider } from 'core/contexts/GlobalConfig';
import { ENV } from 'core/consts';
import { StyledEngineProvider } from '@mui/material';
import { isLocalEnv } from 'core/helpers';
import { runCacheBusterDiagnostics } from 'core/helpers/cacheDiagnostics';
import { Layout } from './components/layout';
import i18n from './core/i18n';
import pkg from '../package.json';

// @Note: Listening on browser bfCache and force a reload if the user has logged out.
const preventHistoryBackLoggedOut = (event: PageTransitionEvent): void => {
  if (event.persisted && !document.cookie.includes('jwtAccessToken')) window.location.reload();
};

const { version } = pkg;

let isGlobalListenerAttached = false;

if (!isGlobalListenerAttached) {
  window.addEventListener('pageshow', preventHistoryBackLoggedOut);
  isGlobalListenerAttached = true;
}

const fidesThemeOptions = fidesThemeOptionsJson as FidesThemeOptions;

const theme: FidesTheme = createFidesTheme(fidesThemeOptions);

runCacheBusterDiagnostics({
  currentVersion: version,
  isLocalEnv,
});

createRoot(document.getElementById('root')).render(
  <CacheBuster currentVersion={version} isVerboseMode isEnabled metaFileDirectory={isLocalEnv ? '' : '/portal/app'}>
    <GlobalConfigProvider fidesTheme={theme}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <I18nextProvider i18n={i18n}>
              <Router basename={ENV.SUB_FOLDER}>
                <Layout />
              </Router>
            </I18nextProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </GlobalConfigProvider>
  </CacheBuster>
);
